import axios, { AxiosResponse } from 'axios';
import {
  User,
  UserLog,
  UsersResponse,
  LogsResponse,
  CreateUserRequest,
  UpdateUserRequest,
  SetAdminRequest,
  AdjustBalanceRequest,
  LogStats,
  SystemStats,
  ApiResponse,
  RegisterRequest,
  RegisterResponse,
  ApiKey,
  ApiKeysResponse,
  CreateApiKeyRequest,
  UpdateApiKeyRequest,
  // API日志相关类型
  APILog,
  APILogsResponse,
  APILogStats,
  // 权限相关类型
  Permission,
  PermissionsResponse,
  CreatePermissionRequest,
  UpdatePermissionRequest,
  PermissionTreeNode,
  PermissionTreeResponse,
  CreatePermissionTreeRequest,
  Role,
  RolesResponse,
  CreateRoleRequest,
  UpdateRoleRequest,
  AssignPermissionsToRoleRequest,
  AssignUsersToRoleRequest,
  AssignRolesToUserRequest,
  UserPermissionsResponse,
  RolePermissionsResponse,
  RoleUsersResponse,
  UserRolesResponse,
  // 大模型管理相关类型
  ModelConfig,
  ModelsResponse,
  CreateModelRequest,
  UpdateModelRequest,
  BatchOperationRequest,
  ModelStats,
  ProviderStats,
  ModelResponse,
  ModelStatsResponse,
  ProviderStatsResponse,
  // 用户模型偏好相关类型
  UserModelPreferences,
  UserModelUsageStats,
  UserModelPreferencesResponse,
  UserModelUsageStatsResponse,
  ModelOption,
  SupportedModelsResponse,
  GenerateTextRequest,
  GenerateTextResponse,
  GenerateEmbeddingRequest,
  GenerateEmbeddingResponse,
  // 聊天相关类型
  ChatSession,
  ChatMessage,
  ChatSessionsResponse,
  ChatMessagesResponse,
  CreateChatSessionRequest,
  UpdateChatSessionRequest,
  SendMessageRequest,
  SendMessageResponse,
  BillingRecord,
  BillingRecordsResponse,
} from '../types';

// 环境变量调试信息
console.log('🔍 环境变量调试信息:', {
  NODE_ENV: process.env.NODE_ENV,
  REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
  实际使用的baseURL: process.env.REACT_APP_API_BASE_URL || 'https://apilink.haotunet.com'
});

// 创建axios实例
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'https://apilink.haotunet.com',
  timeout: 30000, // 增加超时时间到30秒，防止大模型更新等操作超时
});

// 请求拦截器 - 添加认证头
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // 确保Content-Type正确设置
    if (!config.headers['Content-Type'] && config.data) {
      config.headers['Content-Type'] = 'application/json';
    }

    // 添加调试信息
    console.log('API请求配置:', {
      url: config.url,
      method: config.method,
      data: config.data,
      headers: config.headers
    });

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器 - 处理错误
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// 用户管理API
export const userApi = {
  // 获取用户列表
  getUsers: (params: {
    page?: number;
    page_size?: number;
    search?: string;
    role?: string;
  }): Promise<AxiosResponse<{ users: User[]; total: number }>> => {
    return api.get('/api/admin/v1/users', { params });
  },

  // 创建用户
  createUser: (data: CreateUserRequest): Promise<AxiosResponse<User>> => {
    return api.post('/api/admin/v1/users', data);
  },

  // 获取用户详情
  getUser: (id: number): Promise<AxiosResponse<User>> => {
    return api.get(`/api/admin/v1/users/${id}`);
  },

  // 更新用户信息
  updateUser: (id: number, data: UpdateUserRequest): Promise<AxiosResponse<ApiResponse>> => {
    return api.put(`/api/admin/v1/users/${id}`, data);
  },

  // 删除用户
  deleteUser: (id: number): Promise<AxiosResponse<ApiResponse>> => {
    return api.delete(`/api/admin/v1/users/${id}`);
  },

  // 设置管理员权限
  setAdmin: (id: number, data: SetAdminRequest): Promise<AxiosResponse<ApiResponse>> => {
    return api.post(`/api/admin/v1/users/${id}/admin`, data);
  },

  // 调整用户余额
  adjustBalance: (id: number, data: AdjustBalanceRequest): Promise<AxiosResponse<ApiResponse>> => {
    return api.post(`/api/admin/v1/balance/${id}/adjust`, data);
  },

  changePassword: (data: { old_password: string; new_password: string; confirm_password: string }) =>
    api.post('/api/admin/v1/user/change-password', data),
};

// 日志管理API
export const logApi = {
  // 获取所有日志
  getAllLogs: (params: {
    page?: number;
    page_size?: number;
    user_id?: number;
    username?: string;
    action?: string;
    ip?: string;
    details?: string;
    start_time?: string;
    end_time?: string;
  }): Promise<AxiosResponse<LogsResponse>> => {
    return api.get('/api/admin/v1/logs', { params });
  },

  // 获取指定用户的日志
  getUserLogs: (userId: number, page: number = 1, pageSize: number = 10): Promise<AxiosResponse<LogsResponse>> => {
    return api.get(`/api/admin/v1/logs/user/${userId}?page=${page}&page_size=${pageSize}`);
  },

  // 获取日志统计信息
  getLogStats: (params?: { start_time?: string; end_time?: string; username?: string }): Promise<AxiosResponse<LogStats>> => {
    return api.get('/api/admin/v1/logs/stats', { params });
  },

  // 获取系统统计信息
  getSystemStats: (): Promise<AxiosResponse<SystemStats>> => {
    return api.get('/api/admin/v1/system/stats');
  },

  // 获取每日日志统计
  getDailyLogStats: (days: number = 30): Promise<AxiosResponse<Record<string, number>>> => {
    return api.get(`/api/admin/v1/logs/daily-stats?days=${days}`);
  },
};

// API调用日志管理API
export const apiLogApi = {
  // 获取API调用日志（用户看自己的，管理员看所有）
  getAPILogs: (params: {
    page?: number;
    page_size?: number;
    user_id?: number;
    endpoint?: string;
    status_code?: number;
    start_time?: string;
    end_time?: string;
  }): Promise<AxiosResponse<APILogsResponse>> => {
    return api.get('/api/admin/v1/api-logs', { params });
  },

  // 获取指定用户的API调用日志（仅管理员）
  getUserAPILogs: (userId: number, page: number = 1, pageSize: number = 10): Promise<AxiosResponse<APILogsResponse>> => {
    return api.get(`/api/admin/v1/api-logs/user/${userId}?page=${page}&page_size=${pageSize}`);
  },

  // 获取API日志统计信息
  getAPILogStats: (): Promise<AxiosResponse<APILogStats>> => {
    return api.get('/api/admin/v1/api-logs/stats');
  },
};

// 登录请求参数类型
interface LoginParams {
  api_key?: string;
  username?: string;
  password?: string;
  captcha_id?: string;
  captcha_value?: string;
}

// 认证API
export const authApi = {
  // 用户注册
  register: (data: RegisterRequest): Promise<AxiosResponse<RegisterResponse>> => {
    return api.post('/api/v1/auth/register', data);
  },

  // 管理员登录 - 支持API Key或用户名密码
  login: (params: LoginParams): Promise<AxiosResponse<{
    access_token: string;
    refresh_token: string;
    expires_in: number;
    token_type: string;
    user: {
      id: number;
      name: string;
      balance: number;
      is_admin: boolean;
    };
  }>> => {
    return api.post('/api/admin/auth/login', params);
  },

  // 获取验证码
  getCaptcha: (): Promise<AxiosResponse<{
    id: string;
    image: string;
    expire: number;
  }>> => {
    return api.get('/api/admin/auth/captcha');
  },

  // 刷新token
  refreshToken: (refreshToken: string): Promise<AxiosResponse<{
    access_token: string;
    expires_in: number;
    token_type: string;
  }>> => {
    return api.post('/api/admin/auth/refresh', { refresh_token: refreshToken });
  },

  // 登出
  logout: (): Promise<AxiosResponse<{ message: string }>> => {
    return api.post('/api/admin/auth/logout');
  },

  // 获取当前用户信息
  getCurrentUser: (): Promise<AxiosResponse<{
    id: number;
    name: string;
    balance: number;
    is_admin: boolean;
  }>> => {
    return api.get('/api/admin/auth/me');
  },
};

// API密钥管理API
export const apiKeyApi = {
  // 获取所有API密钥列表（管理员用）
  getAllApiKeys: (page: number = 1, pageSize: number = 10): Promise<AxiosResponse<ApiKeysResponse>> => {
    return api.get(`/api/admin/v1/apis?page=${page}&page_size=${pageSize}`);
  },

  // 获取指定用户的API密钥列表
  getUserApiKeys: (userId: number, page: number = 1, pageSize: number = 10): Promise<AxiosResponse<ApiKeysResponse>> => {
    return api.get(`/api/admin/v1/users/${userId}/apis?page=${page}&page_size=${pageSize}`);
  },

  // 为用户创建API密钥
  createApiKey: (userId: number, data: CreateApiKeyRequest): Promise<AxiosResponse<ApiKey>> => {
    return api.post(`/api/admin/v1/users/${userId}/apis`, data);
  },

  // 获取API密钥详情
  getApiKey: (id: number): Promise<AxiosResponse<ApiKey>> => {
    return api.get(`/api/admin/v1/apis/${id}`);
  },

  // 更新API密钥信息
  updateApiKey: (id: number, data: UpdateApiKeyRequest): Promise<AxiosResponse<ApiResponse>> => {
    return api.put(`/api/admin/v1/apis/${id}`, data);
  },

  // 删除API密钥
  deleteApiKey: (id: number): Promise<AxiosResponse<ApiResponse>> => {
    return api.delete(`/api/admin/v1/apis/${id}`);
  },

  // 重新生成API密钥
  regenerateApiKey: (id: number): Promise<AxiosResponse<{ new_key: string }>> => {
    return api.post(`/api/admin/v1/apis/${id}/regenerate`);
  },

  // 切换API密钥状态
  toggleApiKeyStatus: (id: number, isActive: boolean): Promise<AxiosResponse<ApiResponse>> => {
    return api.post(`/api/admin/v1/apis/${id}/toggle`, { is_active: isActive });
  },

  // 智能获取或创建用户API密钥（管理员接口）
  getOrCreateApiKeyForUser: (userId: number): Promise<AxiosResponse<ApiKey>> => {
    return api.get(`/api/admin/v1/users/${userId}/apis/auto`);
  },

  // 智能获取或创建当前用户API密钥（用户接口）
  getOrCreateMyApiKey: (): Promise<AxiosResponse<ApiKey>> => {
    return api.get('/api/v1/user/api-key/auto');
  },
};

// ===== 权限管理API =====
export const permissionApi = {
  // 获取权限列表
  getPermissions: (params: {
    page?: number;
    page_size?: number;
    name?: string;
    category?: string;
    type?: string;
  }): Promise<AxiosResponse<PermissionsResponse>> => {
    return api.get('/api/admin/v1/permissions', { params });
  },

  // 获取所有权限（不分页）
  getAllPermissions: (): Promise<AxiosResponse<{ permissions: Permission[] }>> => {
    return api.get('/api/admin/v1/permissions?page=1&page_size=1000');
  },

  // 创建权限
  createPermission: (data: CreatePermissionRequest): Promise<AxiosResponse<Permission>> => {
    return api.post('/api/admin/v1/permissions', data);
  },

  // 获取权限详情
  getPermission: (id: number): Promise<AxiosResponse<Permission>> => {
    return api.get(`/api/admin/v1/permissions/${id}`);
  },

  // 更新权限信息
  updatePermission: (id: number, data: UpdatePermissionRequest): Promise<AxiosResponse<ApiResponse>> => {
    return api.put(`/api/admin/v1/permissions/${id}`, data);
  },

  // 删除权限
  deletePermission: (id: number): Promise<AxiosResponse<ApiResponse>> => {
    return api.delete(`/api/admin/v1/permissions/${id}`);
  },

  // 获取当前用户权限
  getMyPermissions: (): Promise<AxiosResponse<UserPermissionsResponse>> => {
    return api.get('/api/admin/v1/user-permissions/my');
  },

  // 获取指定用户权限
  getUserPermissions: (userId: number): Promise<AxiosResponse<UserPermissionsResponse>> => {
    return api.get(`/api/admin/v1/user-permissions/${userId}`);
  },

  // ===== 权限树相关API =====

  // 获取权限树结构
  getPermissionTree: (): Promise<AxiosResponse<PermissionTreeResponse>> => {
    return api.get('/api/admin/v1/permissions/tree');
  },

  // 创建权限（树型结构）
  createPermissionTree: (data: CreatePermissionTreeRequest): Promise<AxiosResponse<Permission>> => {
    return api.post('/api/admin/v1/permissions/tree', data);
  },

  // 根据父级获取子权限
  getPermissionsByParent: (parentId?: number): Promise<AxiosResponse<{ permissions: Permission[] }>> => {
    const params = parentId ? `?parent_id=${parentId}` : '';
    return api.get(`/api/admin/v1/permissions/children${params}`);
  },

  // 获取当前用户权限树
  getMyPermissionTree: (): Promise<AxiosResponse<PermissionTreeResponse>> => {
    return api.get('/api/admin/v1/permissions/my-tree');
  },

  // 获取指定用户权限树
  getUserPermissionTree: (userId: number): Promise<AxiosResponse<PermissionTreeResponse>> => {
    return api.get(`/api/admin/v1/permissions/user-tree/${userId}`);
  },
};

// ===== 角色管理API =====
export const roleApi = {
  // 获取角色列表
  getRoles: (page: number = 1, pageSize: number = 10): Promise<AxiosResponse<RolesResponse>> => {
    return api.get(`/api/admin/v1/roles?page=${page}&page_size=${pageSize}`);
  },

  // 获取所有角色（不分页）
  getAllRoles: (): Promise<AxiosResponse<{ roles: Role[] }>> => {
    return api.get('/api/admin/v1/roles?page=1&page_size=1000');
  },

  // 创建角色
  createRole: (data: CreateRoleRequest): Promise<AxiosResponse<Role>> => {
    return api.post('/api/admin/v1/roles', data);
  },

  // 获取角色详情
  getRole: (id: number): Promise<AxiosResponse<Role>> => {
    return api.get(`/api/admin/v1/roles/${id}`);
  },

  // 更新角色信息
  updateRole: (id: number, data: UpdateRoleRequest): Promise<AxiosResponse<ApiResponse>> => {
    return api.put(`/api/admin/v1/roles/${id}`, data);
  },

  // 删除角色
  deleteRole: (id: number): Promise<AxiosResponse<ApiResponse>> => {
    return api.delete(`/api/admin/v1/roles/${id}`);
  },

  // 获取角色权限
  getRolePermissions: (roleId: number): Promise<AxiosResponse<RolePermissionsResponse>> => {
    return api.get(`/api/admin/v1/roles/${roleId}/permissions`);
  },

  // 为角色分配权限
  assignPermissionsToRole: (roleId: number, data: AssignPermissionsToRoleRequest): Promise<AxiosResponse<ApiResponse>> => {
    return api.post(`/api/admin/v1/roles/${roleId}/permissions`, data);
  },

  // 移除角色权限
  removePermissionsFromRole: (roleId: number, data: AssignPermissionsToRoleRequest): Promise<AxiosResponse<ApiResponse>> => {
    return api.delete(`/api/admin/v1/roles/${roleId}/permissions`, { data });
  },

  // 获取角色用户
  getRoleUsers: (roleId: number): Promise<AxiosResponse<RoleUsersResponse>> => {
    return api.get(`/api/admin/v1/roles/${roleId}/users`);
  },

  // 为角色分配用户
  assignUsersToRole: (roleId: number, data: AssignUsersToRoleRequest): Promise<AxiosResponse<ApiResponse>> => {
    return api.post(`/api/admin/v1/roles/${roleId}/users`, data);
  },

  // 从角色移除用户
  removeUsersFromRole: (roleId: number, data: AssignUsersToRoleRequest): Promise<AxiosResponse<ApiResponse>> => {
    return api.delete(`/api/admin/v1/roles/${roleId}/users`, { data });
  },

  // 获取用户角色
  getUserRoles: (userId: number): Promise<AxiosResponse<UserRolesResponse>> => {
    return api.get(`/api/admin/v1/users/${userId}/roles`);
  },

  // 为用户分配角色
  assignRolesToUser: (userId: number, data: AssignRolesToUserRequest): Promise<AxiosResponse<ApiResponse>> => {
    return api.post(`/api/admin/v1/users/${userId}/roles`, data);
  },

  // 移除用户角色
  removeRolesFromUser: (userId: number, data: AssignRolesToUserRequest): Promise<AxiosResponse<ApiResponse>> => {
    return api.delete(`/api/admin/v1/users/${userId}/roles`, { data });
  },
};

// ===== 大模型管理API =====
export const modelApi = {
  // 获取大模型列表
  getModels: (
    page: number = 1,
    pageSize: number = 10,
    provider?: string,
    enabled?: boolean,
    modelName?: string
  ): Promise<AxiosResponse<ModelsResponse>> => {
    let url = `/api/admin/v1/models?page=${page}&page_size=${pageSize}`;
    if (provider) url += `&provider=${provider}`;
    if (enabled !== undefined) url += `&enabled=${enabled}`;
    if (modelName) url += `&model_name=${modelName}`;
    return api.get(url);
  },

  // 创建大模型配置
  createModel: (data: CreateModelRequest): Promise<AxiosResponse<ModelResponse>> => {
    return api.post('/api/admin/v1/models', data);
  },

  // 获取大模型详情
  getModel: (id: number): Promise<AxiosResponse<ModelResponse>> => {
    return api.get(`/api/admin/v1/models/${id}`);
  },

  // 更新大模型配置
  updateModel: (id: number, data: UpdateModelRequest): Promise<AxiosResponse<ApiResponse>> => {
    return api.put(`/api/admin/v1/models/${id}`, data);
  },

  // 删除大模型配置
  deleteModel: (id: number): Promise<AxiosResponse<ApiResponse>> => {
    return api.delete(`/api/admin/v1/models/${id}`);
  },

  // 切换大模型状态
  toggleModelStatus: (id: number): Promise<AxiosResponse<ApiResponse>> => {
    return api.post(`/api/admin/v1/models/${id}/toggle`);
  },

  // 获取大模型统计信息
  getModelStats: (): Promise<AxiosResponse<ModelStatsResponse>> => {
    return api.get('/api/admin/v1/models/stats');
  },

  // 获取提供商统计信息
  getProviderStats: (): Promise<AxiosResponse<ProviderStatsResponse>> => {
    return api.get('/api/admin/v1/models/provider-stats');
  },

  // 批量更新状态
  batchUpdateStatus: (data: BatchOperationRequest): Promise<AxiosResponse<ApiResponse>> => {
    return api.post('/api/admin/v1/models/batch-status', data);
  },

  // 批量删除
  batchDelete: (data: { ids: number[] }): Promise<AxiosResponse<ApiResponse>> => {
    return api.post('/api/admin/v1/models/batch-delete', data);
  },
};

// ===== 用户模型偏好API =====
export const userPreferencesApi = {
  // 获取当前用户模型偏好
  getMyPreferences: (): Promise<AxiosResponse<UserModelPreferencesResponse>> => {
    return api.get('/api/admin/v1/user/model-preferences');
  },

  // 更新当前用户模型偏好
  updateMyPreferences: (preferences: UserModelPreferences): Promise<AxiosResponse<{ success: boolean; message: string }>> => {
    return api.put('/api/admin/v1/user/model-preferences', preferences);
  },

  // 获取当前用户模型使用统计
  getMyUsageStats: (): Promise<AxiosResponse<UserModelUsageStatsResponse>> => {
    return api.get('/api/admin/v1/user/model-usage-stats');
  },

  // 管理员：获取指定用户模型偏好
  getUserPreferences: (userId: number): Promise<AxiosResponse<UserModelPreferencesResponse>> => {
    return api.get(`/api/admin/v1/users/${userId}/model-preferences`);
  },

  // 管理员：更新指定用户模型偏好
  updateUserPreferences: (userId: number, preferences: UserModelPreferences): Promise<AxiosResponse<{ success: boolean; message: string }>> => {
    return api.put(`/api/admin/v1/users/${userId}/model-preferences`, preferences);
  },

  // 管理员：获取指定用户模型使用统计
  getUserUsageStats: (userId: number): Promise<AxiosResponse<UserModelUsageStatsResponse>> => {
    return api.get(`/api/admin/v1/users/${userId}/model-usage-stats`);
  },
};

// ===== 模型选择API =====
export const modelSelectionApi = {
  // 获取支持的模型列表
  getSupportedModels: (): Promise<AxiosResponse<SupportedModelsResponse>> => {
    return api.get('/api/admin/v1/supported-models');
  },

  // 生成文本（支持模型选择）
  generateText: (request: GenerateTextRequest): Promise<AxiosResponse<GenerateTextResponse>> => {
    return api.post('/api/v1/generate', request);
  },

  // 生成嵌入（支持模型选择）
  generateEmbedding: (request: GenerateEmbeddingRequest): Promise<AxiosResponse<GenerateEmbeddingResponse>> => {
    return api.post('/api/v1/embedding', request);
  },

  // 获取余额
  getBalance: (): Promise<AxiosResponse<{ balance: number }>> => {
    return api.get('/api/v1/balance');
  },

  // 获取使用情况
  getUsage: (): Promise<AxiosResponse<any>> => {
    return api.get('/api/v1/usage');
  },

  // 生成文本（流式）
  generateTextStreamUrl: (): string => {
    return '/api/v1/generate';
  },
};

// ===== 聊天功能API =====
export const chatApi = {
  // 获取聊天会话列表
  getSessions: (params: {
    page?: number;
    page_size?: number;
  } = {}): Promise<AxiosResponse<ChatSessionsResponse>> => {
    return api.get('/api/admin/v1/chat/sessions', { params });
  },

  // 创建新的聊天会话
  createSession: (data: CreateChatSessionRequest): Promise<AxiosResponse<ChatSession>> => {
    return api.post('/api/admin/v1/chat/sessions', data);
  },

  // 获取聊天会话详情
  getSession: (sessionId: number): Promise<AxiosResponse<ChatSession>> => {
    return api.get(`/api/admin/v1/chat/sessions/${sessionId}`);
  },

  // 更新聊天会话
  updateSession: (sessionId: number, data: UpdateChatSessionRequest): Promise<AxiosResponse<ApiResponse>> => {
    return api.put(`/api/admin/v1/chat/sessions/${sessionId}`, data);
  },

  // 删除聊天会话
  deleteSession: (sessionId: number): Promise<AxiosResponse<ApiResponse>> => {
    return api.delete(`/api/admin/v1/chat/sessions/${sessionId}`);
  },

  // 获取会话消息列表
  getMessages: (sessionId: number, params: {
    page?: number;
    page_size?: number;
  } = {}): Promise<AxiosResponse<ChatMessagesResponse>> => {
    return api.get(`/api/admin/v1/chat/sessions/${sessionId}/messages`, { params });
  },

  // 发送消息
  sendMessage: (sessionId: number, data: SendMessageRequest): Promise<AxiosResponse<SendMessageResponse>> => {
    return api.post(`/api/admin/v1/chat/sessions/${sessionId}/messages`, data);
  },

  // 流式发送消息
  sendMessageStream: (sessionId: number, data: SendMessageRequest): string => {
    const url = `${process.env.REACT_APP_API_BASE_URL || 'https://apilink.haotunet.com'}/api/admin/v1/chat/sessions/${sessionId}/stream`;

    // 创建EventSource，将请求数据作为查询参数
    const params = new URLSearchParams();
    params.append('content', data.content);
    if (data.model_name) {
      params.append('model_name', data.model_name);
    }

    return `${url}?${params.toString()}`;
  },

  // 流式聊天（暂时返回URL，后续实现SSE）
  getStreamChatUrl: (sessionId: number): string => {
    return `/api/admin/v1/chat/sessions/${sessionId}/stream`;
  },
};

// 账单管理API
export const billingApi = {
  // 获取账单记录
  getRecords: (params: {
    page?: number;
    page_size?: number;
    user_id?: number;
    model_name?: string;
    type?: string;
    start_time?: string;
    end_time?: string;
  }): Promise<AxiosResponse<BillingRecordsResponse>> => {
    return api.get('/api/admin/v1/billing/records', { params });
  },
};

export default api; 